import styled from 'styled-components'
import { CollapsibleText } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { DescriptionsItemType } from 'antd/es/descriptions'
import { ResidentItem } from '@lmc/ts-common'
import {
  AdjustmentCategoryDescriptionEnum,
  ImpairmentDescriptionEnum
} from '@lmc/validations'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import { ChildrenContainer, ExtraText } from '../styledComponents'

type ReasonableAdjustmentContentProps = {
  impairmentLabelsArray: string[] | undefined
  categoryLabelsArray: string[] | undefined
  details: string | undefined
}

const DESCRIPTION_LINES_FOR_TOGGLE = 3

const ReasonableAdjustmentContent = ({
  impairmentLabelsArray,
  categoryLabelsArray,
  details
}: ReasonableAdjustmentContentProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const hasImpairments = impairmentLabelsArray && impairmentLabelsArray.length > 0
  const hasCategories = categoryLabelsArray && categoryLabelsArray.length > 0
  const hasDetails = Boolean(details)

  return (
    <ChildrenContainer>
      {hasImpairments && (
        <ReasonableAdjustmentSection>
          <SectionTitle level={2}>{t(supConstants.REASONABLE_ADJUSTMENT_IMPAIRMENT)}</SectionTitle>
          <BulletList>
            {impairmentLabelsArray.map((label, idx) => (
              <li key={idx}>
                <Text>{label}</Text>
              </li>
            ))}
          </BulletList>
        </ReasonableAdjustmentSection>
      )}
      {hasCategories && (
        <ReasonableAdjustmentSection>
          <SectionTitle level={2}>{t(supConstants.REASONABLE_ADJUSTMENT_CATEGORY)}</SectionTitle>
          <BulletList>
            {categoryLabelsArray.map((label, idx) => (
              <li key={idx}>
                <Text>{label}</Text>
              </li>
            ))}
          </BulletList>
        </ReasonableAdjustmentSection>
      )}
      {hasDetails && (
        <ReasonableAdjustmentSection>
          <SectionTitle level={2}>{t(supConstants.REASONABLE_ADJUSTMENT_DETAILS)}</SectionTitle>
          <CollapsibleText lines={DESCRIPTION_LINES_FOR_TOGGLE}>
            {details}
          </CollapsibleText>
        </ReasonableAdjustmentSection>
      )}
    </ChildrenContainer>
  )
}

export const getReasonableAdjustmentsViewSectionItems = (
  resident: ResidentItem,
  t: (key: string) => string
): DescriptionsItemType[] => {
  if (!resident.reasonableAdjustments?.length) return []

  return resident.reasonableAdjustments.map((adjustment, index) => {
    const impairmentLabelsArray = adjustment.impairment?.map(
      (code) => ImpairmentDescriptionEnum[code as keyof typeof ImpairmentDescriptionEnum] || code
    )
    const categoryLabelsArray = adjustment.adjustmentCategory?.map(
      (code) => AdjustmentCategoryDescriptionEnum[code as keyof typeof AdjustmentCategoryDescriptionEnum] || code
    )

    return {
      key: `reasonable-adjustment-${index}`,
      label: <Text>{t(supConstants.REASONABLE_ADJUSTMENTS)}</Text>,
      labelStyle: { verticalAlign: 'top' },
      children: (
        <ReasonableAdjustmentContent
          impairmentLabelsArray={impairmentLabelsArray}
          categoryLabelsArray={categoryLabelsArray}
          details={adjustment.details}
        />
      )
    }
  })
}

const SectionTitle = styled(ExtraText)`
  padding-bottom: 6px;
  padding-top: 16px;
`

const ReasonableAdjustmentSection = styled.div`
  display: flex;
  flex-direction: column;
  &:first-child > ${SectionTitle} {
    padding-top: 0;
  }
`

const BulletList = styled.ul`
  margin: 0;
  padding-left: 20px;
`
